import React, { useState } from 'react'
import { Icon } from '@chakra-ui/react'
import { FaFilePdf } from 'react-icons/fa6'
import { format } from 'date-fns'

import {
    ClinicFieldsFragment,
    useQueryPimsRemindersLazyQuery,
} from '~graphql/generated/graphql'
import SharedButton from '~components/shared/buttons/shared-button'
import { eqBoolFilter, eqStringFilter } from '~utils/query-helpers'
import { constructOrderBy } from '~utils/querying'
import { ReminderRecordsPatient } from './reminder-records-pdf-document'
import { useClinic } from '~contexts/clinic-context'

interface Props {
    clinic?: ClinicFieldsFragment
    patient: ReminderRecordsPatient
}

const REMINDERS_PDF_PAGE_SIZE = 1000

export default function ExportRemindersButton({ clinic, patient }: Props) {
    const { clinic: contextClinic } = useClinic()
    const [isGenerating, setIsGenerating] = useState(false)
    const [queryReminders] = useQueryPimsRemindersLazyQuery({
        fetchPolicy: 'no-cache',
    })

    async function handleGeneratePdf(event: React.MouseEvent) {
        event.preventDefault()
        event.stopPropagation()

        setIsGenerating(true)
        try {
            const { data } = await queryReminders({
                variables: {
                    where: [
                        eqStringFilter('patient', patient.id),
                        eqBoolFilter('is_active', true),
                        eqBoolFilter('is_completed', false),
                    ],
                    page: 0,
                    pageSize: REMINDERS_PDF_PAGE_SIZE,
                    orderBy: constructOrderBy('send_date'),
                },
            })

            const reminders = data?.queryPimsReminders || []
            const [{ pdf }, { default: ReminderRecordsPDFDocument }] =
                await Promise.all([
                    import('@react-pdf/renderer'),
                    import('./reminder-records-pdf-document'),
                ])
            const blob = await pdf(
                <ReminderRecordsPDFDocument
                    clinic={clinic || contextClinic}
                    patient={patient}
                    reminders={reminders}
                />,
            ).toBlob()
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `${getSafeFileName(patient.name)} Reminder Records (${format(Date.now(), 'yyyy-MM-dd')}).pdf`
            document.body.appendChild(link)
            link.click()
            link.remove()
            URL.revokeObjectURL(url)
        } finally {
            setIsGenerating(false)
        }
    }

    return (
        <SharedButton
            colorScheme="purple"
            isLoading={isGenerating}
            leftIcon={<Icon as={FaFilePdf} />}
            loadingText="Loading..."
            size="xs"
            variant="subtle"
            onClick={handleGeneratePdf}
        >
            Generate PDF
        </SharedButton>
    )
}

function getSafeFileName(name: string | null | undefined) {
    return (name || 'Patient').replace(/[<>:"/\\|?*]+/g, '').trim()
}
